import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'

import {
  Title,
  Description,
  Wrapper,
  Pencil,
  EditInput,
  Form,
  PreviewTitle,
  PencilButton,
  EditInputWrapper,
  TitleButtonWrapper,
  ActionsWrapper,
  HeaderSeparator,
} from './DocumentHeader.styles'

type EditReturn = Promise<any> | void
type EditHandler = (title: string) => EditReturn

interface TitleInputProps {
  value: string
  placeholder?: string
  onEdit: EditHandler
  onCancel: () => void
}

const TitleInput: React.FC<TitleInputProps> = props => {
  const { value: externalValue, onEdit, placeholder, onCancel } = props

  const [value, setValue] = useState(externalValue)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    textAreaRef.current?.select()
  }, [])

  // Make the text area appear selected when opened and
  // mount the escape button hook
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel()
      }
    }

    window.addEventListener('keydown', handleKeyPress)

    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [onCancel])

  const createSubmit = (form: HTMLFormElement | null) => {
    form?.dispatchEvent(new Event('submit', { cancelable: true }))
  }

  return (
    <EditInputWrapper>
      <PreviewTitle>{value}</PreviewTitle>
      <Form
        onSubmit={e => {
          e.preventDefault()
          onEdit(value.trim())
        }}
      >
        <EditInput
          placeholder={placeholder}
          onChange={e =>
            setValue(
              // Replace double space from triggering a new line
              e.target.value.replaceAll(/\s{2,}/g, ' ')
            )
          }
          onBlur={e => {
            createSubmit(e.currentTarget.form)
          }}
          onKeyPress={e => {
            // Make the "Enter" key submit the change instead of a new line
            if (e.key === 'Enter') {
              e.preventDefault()
              createSubmit(e.currentTarget.form)
            }
          }}
          ref={textAreaRef}
          value={value}
        />
      </Form>
    </EditInputWrapper>
  )
}

interface DocumentHeaderProps {
  className?: string
  title: string
  titlePlaceholder?: string
  description?: string
  onEdit?: EditHandler
  actions?: React.ReactNode
  forceEdit?: boolean
  onClearForceEdit?: () => void
}

const DocumentHeader: React.FC<DocumentHeaderProps> = props => {
  const {
    actions,
    className,
    title,
    description,
    onEdit,
    titlePlaceholder,
    forceEdit,
    onClearForceEdit,
  } = props

  const [isEditing, setEditing] = useState(forceEdit)

  const headerRef = useRef<HTMLHeadingElement>(null)
  let titleElement = <Title>{title}</Title>

  // Set the "isEditing" true when the forceEdit is send
  useEffect(() => {
    if (forceEdit) {
      setEditing(true)
    }
  }, [forceEdit])

  // Call "onClearForceEdit" when the edit is set to false
  useEffect(() => {
    if (isEditing && forceEdit) {
      return () => {
        onClearForceEdit?.()
      }
    }
  }, [isEditing, forceEdit, onClearForceEdit])

  if (isEditing && onEdit) {
    titleElement = (
      <TitleInput
        value={title}
        placeholder={titlePlaceholder}
        onEdit={async newTitle => {
          if (title !== newTitle) {
            await onEdit(newTitle)
          }

          setEditing(false)

          // Make the button gain focus again after the change
          setTimeout(() => {
            headerRef.current?.focus()
          })
        }}
        onCancel={() => {
          setEditing(false)
        }}
      />
    )
  } else if (onEdit) {
    titleElement = (
      <Title tabIndex={0} aria-label={title} ref={headerRef}>
        {title}
      </Title>
    )
  }

  return (
    <>
      <Wrapper className={className} aria-label="Document Header">
        <TitleButtonWrapper>
          {titleElement}
          {onEdit && (
            <PencilButton onClick={() => setEditing(true)} disabled={isEditing}>
              <span className="sr-only">Edit</span>
              <Pencil />
            </PencilButton>
          )}
        </TitleButtonWrapper>
        {description && <Description>{description}</Description>}
        {actions && <ActionsWrapper>{actions}</ActionsWrapper>}
      </Wrapper>
      <HeaderSeparator />
    </>
  )
}

export default styled(DocumentHeader)``
